import React, { useState } from 'react';
import { RouteResult } from './RouteResults';
import { useBookmark } from '../contexts/BookmarkContext';
import { useLanguage } from '../contexts/LanguageContext';

interface BookmarkButtonProps {
  routeResult: RouteResult;
  selectedDate: string;
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
}

export const BookmarkButton: React.FC<BookmarkButtonProps> = ({
  routeResult,
  selectedDate,
  size = 'md',
  showLabel = false
}) => {
  const { addBookmark, removeBookmark, isBookmarked } = useBookmark();
  const { language, t } = useLanguage();
  const [isAnimating, setIsAnimating] = useState(false);
  
  const bookmark = isBookmarked(routeResult, selectedDate);
  const bookmarked = !!bookmark;

  const handleBookmarkToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    
    setIsAnimating(true);
    setTimeout(() => setIsAnimating(false), 300);
    
    if (bookmarked && bookmark) {
      removeBookmark(bookmark.id);
    } else {
      addBookmark(routeResult, selectedDate);
    }
  };

  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6'
  };

  const buttonSizeClasses = {
    sm: 'p-1',
    md: 'p-2',
    lg: 'p-3'
  };

  return (
    <button
      onClick={handleBookmarkToggle}
      className={`${buttonSizeClasses[size]} rounded-full transition-all duration-200 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 ${
        bookmarked ? 'text-red-500 hover:text-red-600' : 'text-gray-400 hover:text-red-500'
      } ${isAnimating ? 'scale-125' : 'scale-100'}`}
      title={bookmarked ? t.removeBookmark : t.addBookmark}
      aria-label={bookmarked ? t.removeBookmark : t.addBookmark}
    >
      <div className="relative">
        {bookmarked ? (
          // Filled heart
          <svg 
            className={`${sizeClasses[size]} transition-all duration-200 ${isAnimating ? 'animate-pulse' : ''}`}
            fill="currentColor" 
            viewBox="0 0 24 24"
          >
            <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
          </svg>
        ) : (
          // Outline heart
          <svg 
            className={`${sizeClasses[size]} transition-all duration-200 ${isAnimating ? 'animate-pulse' : ''}`}
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"/>
          </svg>
        )}
      </div>
      
      {showLabel && (
        <span className={`ml-1 text-xs ${language === 'zh' ? 'font-chinese-sans' : 'font-sans'}`}>
          {bookmarked ? t.bookmarked : t.bookmark}
        </span>
      )}
    </button>
  );
};